import { WsReactBaseService } from '@thewhite/react-base-components';
import { SimpleObjectInterface } from '@thewhite/react-global-module/lib/interfaces';
import { UtilService } from '@thewhite/react-global-module/lib/services';
import { globalStore } from '@thewhite/react-global-module/lib/store';
import moment, { Moment } from 'moment';
import { toast } from 'react-toastify';
import Person from '../../Auth/model/person';
import TimePeriod from '../../Booking/model/timePeriod';
import { HourTime } from '../interfaces/HourTime';
import { TimePeriodMoment } from '../interfaces/timePeriodMoment';

/**
 * Сервис вспомогательных инструментов приложения
 */
export class ToolService {
    /**
     * Формат строки дат, передаваемых в запросах на сервер
     */
    static dateFormatString: string = 'yyyy-MM-DD';
    /**
     * Формат дат для вывода в пользовательском интерфейсе
     */
    static templateDateFormatString: string = 'DD.MM.yyyy';

    /**
     * Краткий формат времени (часы-минуты)
     */
    static shortTimeFormat: string = 'HH:mm';

    /**
     * Формат строки дат со временем, передаваемых в запросах на сервер
     */
    static dateTimeFormatString: string = 'yyyy-MM-DD HH:mm:ss';

    /**
     * Регулярка для проверки телефона (либо заполнен полностью, либо не заполнен вообще)
     */
    static PHONE_REG_EXP: RegExp = /^(((\+7|7|8)+([0-9]){10})|(\+7(_){10}))$/gm;

    /**
     * Получить отображаемое имя пользователя
     * @param userPerson
     */
    static getUserDisplayName(userPerson: Person): string {
        let displayName: string = '';
        if (userPerson.firstName && userPerson.lastName) {
            displayName = `${userPerson.firstName} ${userPerson.lastName}`;
            if (userPerson.middleName) {
                displayName = `${userPerson.firstName} ${userPerson.middleName} ${userPerson.lastName}`;
            }
            return displayName;
        }
        if (userPerson.displayName) displayName = userPerson.displayName;
        return displayName;
    }

    /**
     * Защита роутов по правам доступа
     */
    static routeProtection(): void {
        WsReactBaseService.showToast(toast.error('Недостаточно прав для доступа в данный раздел!'));
        globalStore.goToState('/');
    };

    /**
     * Получить доступные элементы енума
     * @param selectedEnum
     * @param searchString
     */
    static getItemsByEnum(selectedEnum: any, searchString?: string): any[] {
        return Object
            .values(selectedEnum)
            .filter((status: any) => {
                if (!searchString) return status;
                return status.name
                    .toLowerCase()
                    .includes(searchString.toLowerCase());
            });
    };

    /**
     * Получить массив времен для календаря
     */
    static getCalendarTimes(
        from: Moment,
        to: Moment,
        scale: number, // количество секунд
        displayInterval: number, // количество секунд
    ): HourTime[] {
        const hourTimes: HourTime[] = [];

        for (
            let time: Moment = from;
            time.diff(to, 'seconds') <= 0;
            time = time.add(scale, 'seconds')
        ) {
            hourTimes.push({
                name: time.format('HH:mm'),
                value: time.format('HH:mm:ss'),
                isDisplayTime: time.diff(to, 'seconds') % displayInterval === 0,
            });
        }

        return hourTimes;
    }

    /**
     * Периоды пересекаются
     */
    static periodsAreCrossing(periodA: TimePeriod, periodB: TimePeriod): boolean {
        if (
            !periodA.startTime ||
            !periodA.endTime ||
            !periodB.startTime ||
            !periodB.endTime
        ) return false;

        const periodMomentA: TimePeriodMoment = {
            start: moment(periodA.startTime),
            end: moment(periodA.endTime),
        };
        const periodMomentB: TimePeriodMoment = {
            start: moment(periodB.startTime),
            end: moment(periodB.endTime),
        };

        return periodMomentA.start
                .isBetween(periodMomentB.start, periodMomentB.end, undefined, '[)') ||
            periodMomentA.end
                .isBetween(periodMomentB.start, periodMomentB.end, undefined, '(]') ||
            periodMomentB.start
                .isBetween(periodMomentA.start, periodMomentA.end, undefined, '[)') ||
            periodMomentB.end
                .isBetween(periodMomentA.start, periodMomentA.end, undefined, '(]');
    }

    /**
     * Получаем объект с отдельными полями ФИО
     * @param fullName
     */
    static getPersonFromFullName(fullName: string | null): Person | null {
        if (!fullName) return null;
        const names = fullName.split(' ');

        return {
            lastName: names[0],
            firstName: names[1] ?? null,
            middleName: names[2] ?? null,
        };
    }

    /**
     * Получаем фамилию с инициалами из ФИО
     * @param fullName
     */
    static getPersonShortNameFromFullName(fullName: string | null): string {
        return UtilService.concatPersonName(this.getPersonFromFullName(fullName));
    }

    /**
     * Дефолтное значение простого объекта
     * @param objectName
     */
    static getDefaultSimpleObject(objectName?: string): SimpleObjectInterface {
        return {
            name: objectName ?? '---',
            value: '',
        };
    }

    /**
     * Массивы эквивалентны (имеют одинаковые элементы)
     * @param arrayOne
     * @param arrayTwo
     */
    static arraysAreEquivalent<T = string>(arrayOne: T[], arrayTwo: T[]) {
        return arrayOne.length === arrayTwo.length &&
            arrayOne.every((element: T) => {
                return arrayTwo.includes(element);
            });
    }

    /**
     * Обертка setInterval для выполнения действия без задержки
     * @param callback
     * @param interval
     */
    static noDelaySetInterval(callback: () => any, interval: number): number {
        callback();
        return window.setInterval(callback, interval);
    }
}
